import {createSlice, createAsyncThunk, createSelector} from "@reduxjs/toolkit"
import axios from "axios"
import { loadWorkspace } from "./workspaceSlice"
import {dynamicViewSlice} from "./dynamicViewSlice"
import {ensureWorkspaceTemplate} from "../utils/reduxUtilities"

/**
 * a copy of the NotificationType enum found in the Notification entity (~/server/src/entities/Notification.ts)
 *
 * @type {{ARTICLE_SHARE: string, WORKSPACE_ADD: string, WORKSPACE_INVITE: string, POST_REGISTER_INVITE: string, WORKSPACE_INVITE_RESPONSE: string}}
 */
export const NotificationType = {
	ARTICLE_SHARE: "article_share", // Article Share
	WORKSPACE_ADD: "workspace_add", // Added to workspace, formerly 'workspace_invite'
	WORKSPACE_INVITE: "workspace_invite", // when admin is inviting existing member.
	POST_REGISTER_INVITE: "post_reg_inv", // always sent after user registers inviting them to default workspace(s). formerly 'workspace_request'
	WORKSPACE_INVITE_RESPONSE: "workspace_inv_res" // sent to workspace owner when invited member accepts. formerly 'workspace_request_res'
}

/**
 * Takes an array of Notifications fetched from the server, and normalizes them to fit shared client-side components
 *
 * @param notificationsToFilter {Notification}
 * @param sentByUser {boolean} - used to modify markup inside component
 * @returns {Array}
 */
const normalizeRetrievedNotifications = (notificationsToFilter, sentByUser = false) => notificationsToFilter.map(notification => ({
	key: notification.id,
	userName: sentByUser
		? `${notification.receivingUser?.firstName} ${notification.receivingUser?.lastName}`
		: `${notification.sendingUser?.firstName} ${notification.sendingUser?.lastName}`,
	articles: notification.sharedArticles,
	date: new Date(notification.created_at),
	message: notification.message,
	notificationId: notification.id,
	isRead: notification.isRead,
	type: notification.type,
	sendingWorkspace: notification.sendingWorkspace,
	sharedWorkspaceId: notification.sendingWorkspace ? notification.sendingWorkspace.id : undefined,
	isArchived: notification.isArchived,
	isSent: sentByUser
}))

export const sendWorkspaceAccessRequest = createAsyncThunk(
	"notifications/sendWorkspaceAccessRequest",
	async (notificationId, thunkAPI) => {
		try {
			return await axios.post(`api/notifications/request-access/${notificationId}`)
		} catch (e) {
			console.error("Trying to send workspace access request, error: ", e)
			return thunkAPI.rejectWithValue("Failed to send workspace access request")
		}
	}
)

export const acceptWorkspaceInvite = createAsyncThunk(
	"notifications/acceptWorkspaceInvite",
	async (notificationId, thunkAPI) => {
		try {
			return await axios.post(`api/notifications/accept-invite/${notificationId}`)
		} catch (e) {
			console.error("Trying to send workspace access request, error: ", e)
			return thunkAPI.rejectWithValue("Failed to send workspace access request")
		}
	}
)

// Async thunk for fetching notifications
export const fetchUserNotifications = createAsyncThunk(
	"notifications/fetchUserNotifications",
	async (userId, thunkAPI) => {
		try {
			const response = await axios.get(`/api/notifications/${userId}`)
			return response.data
		} catch (error) {
			return thunkAPI.rejectWithValue(error.response.data)
		}
	}
)

export const fetchSentNotifications = createAsyncThunk(
	"notifications/fetchSentNotifications",
	async (_, {getState, rejectWithValue}) => {
		try {
			const state = getState()
			const userId = state.user.value.id

			const {data} = await axios.get(`/api/notifications/sent/${userId}`)
			return data
		} catch (error) {
			return rejectWithValue(error.response.data)
		}
	}
)

export const markNotificationsAsRead = createAsyncThunk(
	"notifications/markAsRead",
	async (notificationId, { rejectWithValue }) => {
		try {
			await axios.patch(`/api/notifications/${notificationId}/read`)
			return notificationId
		} catch (error) {
			return rejectWithValue(error.response.data)
		}
	}
)

export const markAsArchived = createAsyncThunk(
	"notifications/markAsArchived",
	async (notificationId, { rejectWithValue }) => {
		try {
			await axios.patch(`/api/notifications/${notificationId}/archive`)
			return notificationId
		} catch (error) {
			return rejectWithValue(error.response.data)
		}
	}
)

export const markAsUnarchived = createAsyncThunk(
	"notifications/markAsUnarchived",
	async (notificationId, { rejectWithValue }) => {
		try {
			await axios.patch(`/api/notifications/${notificationId}/unarchive`)
			return notificationId
		} catch (error) {
			return rejectWithValue(error.response.data)
		}
	}
)

export const getRawNotification = createAsyncThunk(
	"notifications/getRawNotification",
	async (normalizedNotification, { rejectWithValue, getState, dispatch }) => {
		const state = getState()
		const rawNotifications = state.notifications.items
		const rawNotification = rawNotifications.find(notification => notification.id === normalizedNotification.notificationId)
		if (rawNotification) {
			dispatch(markNotificationsAsRead(rawNotification.id))
			return rawNotification
		} else {
			console.error("FAILED TO GET RAW NOTIFICATION")
			return rejectWithValue("Failed to find raw notification")
		}
	}
)

const initialState = {
	currentPanelView: 0,
	targetWorkspace: null,
	sharedArticlesWorkspaceId: null,
}

const ensureWorkspace = (state, workspaceId) => ensureWorkspaceTemplate(state, workspaceId, initialState)

export const notificationsSlice = createSlice({
	name: "notifications",
	initialState: {
		items: [],
		sentItems:[],
		status: "idle",
		error: null,
		notificationViews: [
			"In",
			"Archive",
			"Sent",
		],
		openSwitchWorkspaceModal: false,
		byWorkspace: {}
	},
	reducers: {
		receivedNewNotification: (state, action) => {
			// unshift ensures that new notifications will be at front of Array, not back.
			state.items.unshift(action.payload)
		},
		changePanelView: (state, action) => {
			const {index, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.currentPanelView = index
		},
		setTargetWorkspace: (state, action) => {
			const {targetWorkspaceId, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.targetWorkspace = targetWorkspaceId
		},
		setSwitchWorkspaceModal: (state, action) => {
			state.openSwitchWorkspaceModal = action.payload
		},
		setSharedArticlesWorkspaceId: (state, action) => {
			const {sharedId, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.sharedArticlesWorkspaceId = sharedId
		},
		resetSharedArticlesWorkspaceId: (state, action) => {
			const workspace = ensureWorkspace(state, action.payload)
			workspace.sharedArticlesWorkspaceId = null
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchUserNotifications.pending, (state) => {
				state.status = "loading"
			})
			.addCase(fetchUserNotifications.fulfilled, (state, action) => {
				state.status = "succeeded"
				state.items = action.payload
			})
			.addCase(fetchUserNotifications.rejected, (state, action) => {
				state.status = "failed"
				state.error = action.payload || "Failed to fetch notifications"
			})
			.addCase(markNotificationsAsRead.fulfilled, (state, action) => {
				const notificationIndex = state.items.findIndex(notification => notification.id === action.payload)
				if (notificationIndex >= 0) {
					state.items[notificationIndex].isRead = true
				}
			})
			.addCase(markAsArchived.fulfilled, (state, action) => {
				const notificationIndex = state.items.findIndex(notification => notification.id === action.payload)
				if (notificationIndex >= 0) {
					state.items[notificationIndex].isArchived = true
				}
			})
			.addCase(markAsUnarchived.fulfilled, (state, action) => {
				const notificationIndex = state.items.findIndex(notification => notification.id === action.payload)
				if (notificationIndex >= 0) {
					state.items[notificationIndex].isArchived = false
				}
			})
			.addCase(fetchSentNotifications.pending, (state) => {
				state.status = "loading"
			})
			.addCase(fetchSentNotifications.fulfilled, (state, action) => {
				state.status = "succeeded"
				state.sentItems = action.payload
			})
			.addCase(fetchSentNotifications.rejected, (state, action) => {
				console.error("FAILED TO FETCH SENT NOTIFICATIONS")
				state.error = action.payload || "Failed to fetch sent notifications"
			})
			.addCase( // If user creates or moves a new workspace, reset notification flags
				loadWorkspace.fulfilled,
				(state) => {
					state.openSwitchWorkspaceModal = false
				}
			)
			.addCase( // if user switches dynamic view panels, reset notification flags
				dynamicViewSlice.actions.setDynamicView,
				(state, action) => {
					const workspace = ensureWorkspace(state, action.payload.workspaceId)
					workspace.sharedArticlesWorkspaceId = initialState.sharedArticlesWorkspaceId
				}
			)
			.addCase(
				sendWorkspaceAccessRequest.rejected,
				(state, action) => {
					state.error = action.payload || "Failed to send workspace access request"
				}
			)
			.addCase(
				acceptWorkspaceInvite.rejected,
				(state, action) => {
					state.error = action.payload || "Failed to accept workspace invite"
				}
			)

	}
})

export const {
	receivedNewNotification,
	changePanelView,
	setTargetWorkspace,
	setSwitchWorkspaceModal,
	setSharedArticlesWorkspaceId,
	resetSharedArticlesWorkspaceId,
} = notificationsSlice.actions

// Selectors
export const selectStatus = state => state.notifications.status
export const selectError = state => state.notifications.error
export const selectCurrentPanelView = (state, workspaceId) => state.notifications.byWorkspace[workspaceId]?.currentPanelView ?? initialState.currentPanelView
export const selectAllPanelViews = (state) => state.notifications.notificationViews
export const selectSentNotifications = state => state.notifications.sentItems
export const selectTargetWorkspace = (state, workspaceId) => state.notifications.byWorkspace[workspaceId]?.targetWorkspace ?? initialState.targetWorkspace
export const selectOpenSwitchWorkspaceModal = state => state.notifications.openSwitchWorkspaceModal
export const selectSharedArticlesWorkspaceId = (state, workspaceId) => state.notifications.byWorkspace[workspaceId]?.sharedArticlesWorkspaceId ?? initialState.sharedArticlesWorkspaceId


// local selector to get all received notifications from the state
const selectNotifications = (state) => state.notifications.items

// Memoized selector that returns notifications filtered depending on the current panel view index.
// this logic used to be done in the NotificationPanel component, but rendering / memoization issues over-complicated that.
// Notification entities are also normalized before being returned using an Array.map function in a utility method
//
// read more about why we use this and not 'selectAllNotifications' in components here:
// https://javascript.plainenglish.io/boosting-redux-performance-with-memoized-selectors-best-practices-and-implementation-tips-51b4cf59d47c
export const selectFilteredNotificationsForCurrentPanelView = (workspaceId) => createSelector(
	[
		selectNotifications,
		(state) => selectCurrentPanelView(state, workspaceId),
		selectSentNotifications
	],
	(notifications, currentPanelIndex, sentNotifications) => {
		let filteredNotifications

		if (currentPanelIndex === 0) {
			filteredNotifications = notifications.filter(notification => !notification.isArchived)
			return normalizeRetrievedNotifications(filteredNotifications, false)
		}
		if (currentPanelIndex === 1) {
			filteredNotifications = notifications.filter(notification => notification.isArchived)
			return normalizeRetrievedNotifications(filteredNotifications, false)
		}
		if (currentPanelIndex === 2) {
			filteredNotifications = sentNotifications
			return normalizeRetrievedNotifications(filteredNotifications, true)
		}
	}
)

// Memoized selector to filter unread notifications
// NOTE: this is used in the DynamicPanel to set the unread notifs badge.
export const selectUnreadNotifications = createSelector(
	[selectNotifications],
	(notifications) => notifications.filter(notification => !notification.isRead)
)

// Reducer export
export default notificationsSlice.reducer
